import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, PlusCircle, Search } from 'lucide-react';
import { normalize } from '../utils/importParsing';

/**
 * Selector de producto buscable, usado en los importadores para resolver a mano
 * las filas que el matching automático no pudo asignar.
 *
 * El panel se renderiza en un PORTAL con posición fija, no como hijo del botón.
 * Dentro de una tabla con scroll propio, un panel `absolute` queda recortado por
 * el contenedor y aparece "tapado" por la fila siguiente; el portal lo saca de
 * ese contexto. La posición se recalcula al scrollear y al redimensionar, y el
 * panel se da vuelta hacia arriba cuando no entra abajo.
 *
 * options: [{ id, label, barcode }]
 * onCreate: opcional. Si viene, se ofrece "Crear producto nuevo" con el texto
 *           filtrado, para las filas del archivo que no existen en el catálogo.
 */

const MARGEN = 8;
const ALTO_MAXIMO = 320;
const ALTO_MINIMO = 180;

export default function ProductCombo({
  value,
  options,
  onChange,
  placeholder = 'Seleccionar…',
  onCreate = null,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const anclaRef = useRef(null);
  const panelRef = useRef(null);

  const calcularPosicion = useCallback(() => {
    const ancla = anclaRef.current;
    if (!ancla) return;

    const rect = ancla.getBoundingClientRect();
    const espacioAbajo = window.innerHeight - rect.bottom - MARGEN;
    const espacioArriba = rect.top - MARGEN;
    const haciaArriba = espacioAbajo < ALTO_MINIMO && espacioArriba > espacioAbajo;
    const alto = Math.min(ALTO_MAXIMO, Math.max(haciaArriba ? espacioArriba : espacioAbajo, ALTO_MINIMO));

    // Se mantiene dentro del viewport también en horizontal.
    const ancho = Math.max(rect.width, 280);
    const left = Math.min(Math.max(MARGEN, rect.left), window.innerWidth - ancho - MARGEN);

    setPos({
      left,
      ancho,
      alto,
      haciaArriba,
      top: haciaArriba ? rect.top - MARGEN : rect.bottom + MARGEN,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    calcularPosicion();

    const alMover = () => calcularPosicion();
    // capture: true para enterarse también del scroll del contenedor de la tabla.
    window.addEventListener('scroll', alMover, true);
    window.addEventListener('resize', alMover);

    const alClickear = (e) => {
      if (anclaRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const alTeclear = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', alClickear);
    document.addEventListener('keydown', alTeclear);

    return () => {
      window.removeEventListener('scroll', alMover, true);
      window.removeEventListener('resize', alMover);
      document.removeEventListener('mousedown', alClickear);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [open, calcularPosicion]);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const nq = normalize(q);
    if (!nq) return options.slice(0, 30);
    return options
      .filter((o) => normalize(o.label).includes(nq) || normalize(o.barcode).includes(nq))
      .slice(0, 30);
  }, [options, q]);

  const cerrar = () => {
    setOpen(false);
    setQ('');
  };

  const panel = open && pos ? createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.haciaArriba ? undefined : pos.top,
        bottom: pos.haciaArriba ? window.innerHeight - pos.top : undefined,
        width: pos.ancho,
        maxHeight: pos.alto,
        zIndex: 9999,
      }}
      className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-slate-100 shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-slate-300" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar por nombre o código…"
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-[11px] font-bold outline-none focus:border-[#51A7FD]"
          />
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.25em]">
            Sin resultados
          </div>
        ) : (
          filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                onChange(o.id);
                cerrar();
              }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-all"
            >
              <div className="text-[11px] font-black text-slate-800 truncate">{o.label}</div>
              <div className="text-[10px] font-bold text-slate-400 truncate">
                {o.barcode ? `Barras: ${o.barcode}` : 'Sin barras'}
              </div>
            </button>
          ))
        )}
      </div>

      {onCreate && (
        <button
          type="button"
          onClick={() => {
            const texto = q.trim();
            cerrar();
            onCreate(texto);
          }}
          className="shrink-0 w-full flex items-center gap-2 px-3 py-2.5 border-t border-slate-100 bg-emerald-50/60 text-emerald-700 hover:bg-emerald-50 transition-all"
        >
          <PlusCircle size={15} />
          <span className="text-[10px] font-black uppercase tracking-widest">
            Crear producto nuevo
          </span>
        </button>
      )}

      <button
        type="button"
        onClick={cerrar}
        className="shrink-0 w-full px-3 py-2 bg-slate-50 border-t border-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-slate-700"
      >
        Cerrar
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative w-full">
      <button
        ref={anclaRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-800 shadow-sm hover:bg-slate-50 transition-all"
      >
        <div className="min-w-0 text-left">
          <div className="text-[11px] font-black truncate">
            {selected ? selected.label : <span className="text-slate-400">{placeholder}</span>}
          </div>
          {selected ? (
            <div className="text-[10px] font-bold text-slate-400 truncate">
              {selected.barcode ? `Barras: ${selected.barcode}` : 'Sin barras'}
            </div>
          ) : (
            <div className="text-[10px] font-bold text-slate-300 truncate">Click para buscar y asignar</div>
          )}
        </div>
        <ChevronDown className="text-slate-300 shrink-0" size={16} />
      </button>

      {panel}
    </div>
  );
}
